import { useRouter } from "next/router";
import { DownloadSimple, CaretDown, X } from "@phosphor-icons/react";
import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { hoverScale, tapEffect, fadeUp, staggerContainer } from "lib/animations";
import Image from "next/image";
import Link from "next/link";
import { createClassroomSlug } from "utils/slugify";

import student1 from "../public/img/landing/landing_student_1.png";
import student2 from "../public/img/landing/landing_student_2.png";
import student3 from "../public/img/landing/landing_student_3.png";
import student4 from "../public/img/landing/landing_student_4.png";

import ioeLogo from "../public/img/exams/ioe.png";
import csitLogo from "../public/img/exams/csit.png";

import testSaksham from "../public/img/testimonial/Saksham.jpg";
import testSandip from "../public/img/testimonial/SandipGewali.jpg";
import testNishant from "../public/img/testimonial/Nishant.jpg";

import appStoreLogo from "../public/img/logo/appstore.png";
import playStoreLogo from "../public/img/logo/playstore.png";

export const LANDING_IMAGES = [
  student1,
  student2,
  student3,
  student4,
];

const HeroSection = ({
  courses,
  heroImageIndex = 0,
}: {
  courses: any[];
  heroImageIndex?: number;
}) => {
  const router = useRouter();
  const [showCoursePopup, setShowCoursePopup] = useState(false);
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);
  const downloadRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        downloadRef.current &&
        !downloadRef.current.contains(e.target as Node)
      ) {
        setShowDownloadDropdown(false);
      }
    };

    document.addEventListener("mousedown", handler);

    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <>
      <div className="hero-root">
        {/* Main Grid */}
        <div className="hero-grid relative z-10">
          {/* LEFT — one authored entrance for the whole column: a confident,
              fast stagger (headline leads, then proof, then the ask), not a
              repeated scroll-reveal. Above the fold, so it plays on load,
              not on scroll into view. Framer Motion respects the global
              prefers-reduced-motion media query by falling back to its
              default instant transition when the OS setting disables
              animation, since MotionConfig isn't overriding that here. */}
          <motion.div
            className="hero-left"
            variants={staggerContainer}
            initial="hidden"
            animate="show"
          >
            {/* Heading */}
            <motion.h1
              variants={fadeUp}
              className="text-2xl sm:text-3xl lg:text-4xl font-black text-gray-900 text-center lg:text-left"
            >
              Best IOE &amp; CSIT Entrance Preparation in Kathmandu and Online
            </motion.h1>

            {/* Description */}
            <motion.p
              variants={fadeUp}
              className="text-base sm:text-xl text-gray-600 max-w-2xl mx-auto lg:mx-0 leading-relaxed mt-5"
            >
              Nepal&apos;s best{" "}
              <Link href="/ioe-entrance-preparation" className="font-bold text-gray-900 hover:text-primary underline decoration-primary/30 underline-offset-2">
                IOE
              </Link>{" "}
              and{" "}
              <Link href="/csit-entrance-preparation" className="font-bold text-gray-900 hover:text-primary underline decoration-primary/30 underline-offset-2">
                CSIT
              </Link>{" "}
              entrance prep platform.{" "}
              <Link href="/ioe-mock-test" className="font-bold text-gray-900 hover:text-primary underline decoration-primary/30 underline-offset-2">
                Free mock tests
              </Link>
              , top mentors, a study planner, online Nepalwide, physical in Kathmandu.
            </motion.p>

            {/* Exam Categories */}
            <motion.p
              variants={fadeUp}
              className="mt-6 text-xs uppercase tracking-[0.2em] text-gray-500 font-semibold text-center lg:text-left"
            >
              Preparation Available For
            </motion.p>

            {/* Exam Logos */}
            <motion.div variants={fadeUp} className="mt-3 flex flex-wrap items-center justify-center lg:justify-start gap-3">
              {[
                {
                  name: "IOE Pre Engineering",
                  logo: ioeLogo,
                  url: "/classrooms/clamphook-pre-engineering-2083-grade-xii-online-6a40fbfd067c38f5afcd63ce"
                },
                {
                  name: "IOE Msc Entrance",
                  logo: csitLogo,
                  url: "/classrooms/clamphook-ioe-msc-entrance-preparation-2083-66ee2d69dc91087f0e5d3ef9"
                },
              ].map((exam) => (
                <motion.div
                  whileHover={hoverScale}
                  whileTap={tapEffect}
                  key={exam.name}
                  onClick={() => { window.location.href = exam.url }}
                  className="flex items-center gap-2 bg-primary/10 backdrop-blur-sm border border-primary rounded-2xl px-4 py-2 shadow-sm hover:shadow-md transition duration-300 cursor-pointer"
                >
                  <Image
                    src={exam.logo}
                    alt={`${exam.name} Entrance Preparation`}
                    sizes="56px"
                    quality={60}
                    loading="lazy"
                    className="w-7 h-7 object-contain"
                  />

                  <span className="text-sm font-semibold text-gray-700">
                    {exam.name}
                  </span>
                </motion.div>
              ))}
            </motion.div>

            {/* Social proof -- matches the same real testimonial photos/copy
                already live on /register, just restored here (this block
                was dead/commented out, leaving the hero with zero visible
                proof above the fold). */}
            <motion.div variants={fadeUp} className="mt-4 flex flex-col items-center lg:flex-row lg:items-center gap-3">
              <div className="flex flex-shrink-0 -space-x-3">
                {[
                  { name: "Saksham", img: testSaksham },
                  { name: "SandipGewali", img: testSandip },
                  { name: "Nishant", img: testNishant }
                ].map((person) => (
                  // Source files are 2268x2268 but render at 44x44. next/image
                  // serves an 88px-wide variant (2x retina) instead.
                  <Image
                    key={person.name}
                    src={person.img}
                    alt={`${person.name}, Clamphook student`}
                    sizes="88px"
                    loading="lazy"
                    className="inline-block rounded-full w-11 h-11 ring-2 ring-white object-cover"
                  />
                ))}
              </div>

              <p className="text-sm text-gray-600 text-center lg:text-left leading-snug max-w-md">
                Helping 100,000+ students prepare for IOE, CSIT, NEB, and other
                exams across Nepal
              </p>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div variants={fadeUp} className="mt-8 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3">
              {/* Enroll */}
              <motion.button
                whileHover={hoverScale}
                whileTap={tapEffect}
                onClick={() => setShowCoursePopup(true)}
                className="btn-enroll w-full sm:w-auto"
              >
                Enroll Now
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </motion.button>

              {/* Download App */}
              <div ref={downloadRef} className="relative w-full sm:w-auto">
                <motion.button
                  whileHover={hoverScale}
                  whileTap={tapEffect}
                  onClick={() => setShowDownloadDropdown((v) => !v)}
                  className={`btn-download w-full sm:w-auto${showDownloadDropdown ? " open" : ""
                    }`}
                >
                  <DownloadSimple size={18} />

                  <span>Download App</span>

                  <svg
                    className={`chevron${showDownloadDropdown ? " rotated" : ""
                      }`}
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </motion.button>

                {showDownloadDropdown && (
                  <div className="download-dropdown">
                    {/* App Store */}
                    <a
                      href="https://apps.apple.com/us/app/clamphook-academy/id6737482974"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="dropdown-item"
                      onClick={() => setShowDownloadDropdown(false)}
                    >
                      <Image
                        src={appStoreLogo}
                        sizes="48px"
                        loading="lazy"
                        className="w-6 h-6 object-contain"
                        alt="App Store"
                      />

                      <div>
                        <span className="dropdown-item-small">
                          Download on the
                        </span>

                        <span className="dropdown-item-big">App Store</span>
                      </div>
                    </a>

                    {/* Play Store */}
                    <a
                      href="https://play.google.com/store/apps/details?id=com.clamphook.clamphook"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="dropdown-item"
                      onClick={() => setShowDownloadDropdown(false)}
                    >
                      <Image
                        src={playStoreLogo}
                        sizes="48px"
                        loading="lazy"
                        className="w-6 h-6 object-contain"
                        alt="Google Play"
                      />

                      <div>
                        <span className="dropdown-item-small">
                          Download on the
                        </span>

                        <span className="dropdown-item-big">Google Play</span>
                      </div>
                    </a>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Stats — scoreboard treatment: enormous tabular numerals against
                tiny wide-tracked labels, separated by hairline rules rather
                than cards. */}
            <motion.div variants={fadeUp} className="mt-10 pt-8 border-t border-gray-200">
              <p className="mb-5 text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 text-center lg:text-left">
                Kathmandu &amp; Nationwide Online
              </p>
              <div className="grid grid-cols-3 divide-x divide-gray-200 text-center lg:text-left">
                {[
                  { value: "100K+", label: "Students Enrolled" },
                  { value: "30+", label: "Expert Mentors" },
                  { value: "24/7", label: "Learning Access" },
                ].map(({ value, label }, i) => (
                  <div key={label} className={i === 0 ? "pr-4" : "px-4"}>
                    <div
                      className="text-4xl sm:text-5xl font-black text-primary-900 leading-none tracking-tight"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    >
                      {value}
                    </div>

                    <div className="mt-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-gray-500">
                      {label}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>

          {/* RIGHT — landing image, index chosen server-side in
              getStaticProps (rotates every ISR revalidation) so SSR output
              and the hydrated client always show the same file. Picking
              this client-side after mount used to swap out the
              priority-preloaded SSR image for a different, unpreloaded one
              right after hydration, forcing a second fetch that tanked
              mobile LCP.
              Wrapper div offsets the image below the fixed navbar on desktop
              (lg:top-[5.25rem] = 84 px). next/image serves AVIF/WebP so the
              ~9 MB source PNGs never reach the browser. */}
          <div className="relative overflow-hidden h-[70vw] sm:h-[60vw] lg:h-auto lg:min-h-screen">
            <div className="absolute inset-0 lg:top-[3rem] lg:p-8 lg:pt-4">
              <div className="relative h-full w-full lg:border lg:border-gray-200 overflow-hidden rounded-xl">
                <Image
                  src={LANDING_IMAGES[heroImageIndex]}
                  alt="Clamphook Academy student preparing for IOE entrance exam in Nepal"
                  fill
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 60vw, 50vw"
                  className="object-cover object-top"
                  quality={60}
                  priority={true}
                />

                {/* Result-slip stamp */}
                <div className="hidden lg:flex absolute top-6 right-6 flex-col items-end bg-primary-900 text-white px-4 py-2">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-primary-300">
                    Batch
                  </span>
                  <span
                    className="text-2xl font-black leading-none tracking-tight"
                    style={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    2083
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="scroll-indicator pointer-events-none hidden lg:flex flex-col items-center gap-1.5 text-primary/50">
          <span className="text-[10px] font-semibold uppercase tracking-[0.15em]">
            Scroll
          </span>
          <CaretDown size={20} weight="bold" />
        </div>
      </div>

      {/* Course Popup */}
      {showCoursePopup && (
        <div
          className="modal-overlay-anim  fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4"
          onClick={() => setShowCoursePopup(false)}
        >
          <div
            className="modal-box-anim bg-white rounded-2xl shadow-xl w-[90%] max-w-lg p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowCoursePopup(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <X size={16} />
            </button>

            <h2 className="text-2xl font-semibold text-gray-800 mb-5 border-b pb-3 text-center">
              Available Courses
            </h2>

            <div className="space-y-3 overflow-y-auto">
              {Array.isArray(courses) && courses.length > 0 ? (
                courses.map((course: any) => (
                  <div
                    key={course._id.$oid}
                    className="flex items-center justify-between bg-gray-50 hover:bg-primary-50 p-3 rounded-xl transition"
                  >
                    <span className="text-gray-800 font-medium text-sm">
                      {course.name}
                    </span>

                    <button
                      onClick={() =>
                      (window.location.href = `/classrooms/${createClassroomSlug(
                        course.name,
                        course._id.$oid,
                      )}`)
                      }
                      className="bg-primary text-white px-4 py-1.5 rounded-lg font-semibold hover:bg-primary-600 transition text-sm flex-shrink-0 ml-3"
                    >
                      Enroll
                    </button>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-6">
                  No courses available
                </p>
              )}
            </div>
            <hr className="my-4" />
          </div>
        </div>
      )}
    </>
  );
};

export default HeroSection;
